import {
  FileText,
  Archive,
  File as FileIcon,
  Download,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SUBJECT_MAP } from "@/lib/domain-constants";
import dayjs from "dayjs";
import { formatDate, formatDateTime } from "@/lib/format";
import { API_URL } from "@/constants";

/** Etiqueta del objeto contractual en mayúsculas (OBRA / SERVICIO / BIEN). */
export function subjectLabel(subject?: string | null): string {
  if (!subject) return "";
  return (SUBJECT_MAP[subject.toLowerCase()] ?? subject).toUpperCase();
}

function fileIcon(format?: string) {
  const f = (format ?? "").toLowerCase();
  if (f === "pdf")
    return {
      cls: "bg-pink-50 text-pink-600 dark:bg-pink-950/20 dark:text-pink-400",
      Icon: FileText,
    };
  if (["zip", "rar", "7z"].includes(f))
    return {
      cls: "bg-amber-50 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400",
      Icon: Archive,
    };
  return {
    cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    Icon: FileIcon,
  };
}

export function SectionTitle({
  icon,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
      {icon}
      {children}
    </h3>
  );
}

export function DocCard({
  doc,
  onPreview,
  proxyPreview = true,
}: {
  doc: any;
  onPreview?: (url: string, title: string) => void;
  // Remote scraped documents need the proxy-pdf endpoint; locally stored
  // files (served at /files/) are previewed directly.
  proxyPreview?: boolean;
}) {
  const { cls, Icon } = fileIcon(doc.format);
  const isPdf = (doc.format ?? "").toLowerCase() === "pdf";
  return (
    <div className="flex items-center justify-between rounded-2xl border bg-slate-50/50 p-3.5 transition-colors hover:bg-slate-100/60 dark:bg-slate-900/10 dark:hover:bg-slate-900/20">
      <div className="flex min-w-0 items-center gap-3 pr-4">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-xl",
            cls,
          )}
        >
          <Icon className="size-5" />
        </div>
        <div className="flex min-w-0 flex-col">
          <span
            className="truncate text-xs font-bold leading-snug"
            title={doc.title}
          >
            {/* El OECE a veces publica documentos sin título. */}
            {doc.title || "Documento sin nombre"}
          </span>
          <div className="mt-1 flex items-center gap-1.5 text-[9.5px] font-bold text-slate-500">
            {doc.format && (
              <span className="rounded bg-slate-200/60 px-1.5 py-0.5 font-mono text-[8.5px] font-extrabold uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {doc.format}
              </span>
            )}
            {doc.format && doc.datePublished && (
              <span className="font-normal text-slate-300 dark:text-slate-700">
                •
              </span>
            )}
            {doc.datePublished && <span>{documentDate(doc.datePublished)}</span>}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {isPdf && doc.url && (
          <button
            type="button"
            onClick={() =>
              onPreview?.(
                proxyPreview
                  ? `${API_URL}/api/public/proxy-pdf?url=${encodeURIComponent(doc.url)}`
                  : doc.url,
                doc.title,
              )
            }
            title="Visualizar documento"
            className="flex size-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800"
          >
            <Eye className="size-4" />
          </button>
        )}
        {doc.url && (
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            title="Abrir / Descargar"
            className="flex size-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800"
          >
            <Download className="size-4" />
          </a>
        )}
      </div>
    </div>
  );
}

export function Meta({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-extrabold text-slate-800 dark:text-slate-100">
        {children}
      </div>
    </div>
  );
}

/**
 * Fecha del documento con su hora, que el OECE y la ficha del SEACE traen. Si la
 * fecha llega sin hora, queda a las 00:00 y se muestra solo el día.
 */
function documentDate(date: string): string {
  return dayjs(date).format("HH:mm") === "00:00" ? formatDate(date) : formatDateTime(date);
}
